// ============================================
// FILE: src/utils/recentlyViewed.js
// ============================================
//
// Recently-viewed product ids, kept on the shopper's own device.
//
// Deliberately localStorage rather than the backend: it works for signed-out
// shoppers, survives an expired session, and needs no round trip to render.
// Only ids are stored — the rail re-fetches current products from
// /products/by-ids, so prices and stock are never stale.

const KEY = 'recently_viewed_products';
const MAX = 20;

const read = () => {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => id != null) : [];
  } catch {
    // Private mode, cleared storage, or corrupt JSON — start clean.
    return [];
  }
};

const write = (ids) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids.slice(0, MAX)));
  } catch {
    // Quota or blocked storage; a missing history is not worth throwing over.
  }
};

/** Ids of recently viewed products, most recent first. */
export const getRecentlyViewed = () => read();

/**
 * Record a view. Re-viewing a product moves it to the front rather than
 * duplicating it, so the rail reads as a genuine history.
 */
export const addRecentlyViewed = (productId) => {
  if (productId == null || productId === '') return read();

  const id = String(productId);
  const next = [id, ...read().map(String).filter((existing) => existing !== id)];

  write(next);
  return next;
};

/**
 * Ids to show in a rail, excluding the product currently on screen — a
 * "recently viewed" list that leads with the page you are already on is noise.
 */
export const getRecentlyViewedExcluding = (productId, limit = 12) =>
  read()
    .map(String)
    .filter((id) => id !== String(productId))
    .slice(0, limit);

export const clearRecentlyViewed = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
};
